import { useCallback, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { authenticationState, customerState } from '@repo/unicity'
import { customerHelpers } from '@helpers'
import { localize } from '@/core/utils'
import { getFullPath } from '@/core/env.config'
import { userStatusAtom, readStoredUserStatus } from '@/core/auth/user-status.store'
import { useLogin } from '@/core/auth/useLogin'
import { cartAtom, cartEditedAtom, v2CartAtom } from '@/core/cart/cart.store'
import {
    checkoutStepAtom,
    checkoutReferralIdAtom,
    checkoutReferralInfoAtom,
    checkoutUuidAtom,
    checkoutPeriodAtom,
    checkoutLoadingAtom,
    checkoutErrorsAtom
} from '@/core/checkout/checkout.store'
import { fetchFeelGreatSettings } from '@/core/feelgreat/feelgreat.api'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import { hotFormatRawAtom } from '@/core/store/hotFormat.store'
import { getOperatingSystemName } from '@/core/subscriptions/utils/subscription-formatters'
import {
    validateOmanReferrer,
    createOmanUuid,
    getOmanFormat,
    syncOmanShippingMethodFormat,
    updateOmanShippingMethod,
    createOmanAccount,
    putOmanMemberShipping,
    fetchOmanAddressHistory,
    fetchOmanEntryPeriod
} from '../services/oman.api'
import { omanFormatDataAtom, isLoadingOmanFormatAtom, omanSelectedShippingMethodAtom, omanDeliveryFreightAtom } from '../stores/oman.store'
import { isOmanUuidExpiredError, parseOmanAxiosError, parseOmanResultError } from '../utils/oman-error.helper'
import { getShipmentPacks, isV2CartItem } from '../utils/oman-shipment-packs.helper'
import { analytics } from '@/core/analytics/analytics'
import type { TOmanCheckoutForm, TOmanShippingContact, TOmanShippingForm, TOmanShippingMethodId } from '../types/oman.types'
import type { TAddressHistoryResponse } from '@/core/checkout/checkout.types'

const extractFreight = (raw: Record<string, unknown>): number => {
    try {
        const hydra = raw.hydra as Record<string, unknown> | undefined
        const marketing = hydra?.marketing as Record<string, unknown> | undefined
        const terms = marketing?.terms as Record<string, unknown> | undefined
        const freight = terms?.freight as Record<string, unknown> | undefined
        return Number(freight?.amount ?? 0)
    } catch {
        return 0
    }
}

interface SyncShippingMethodOptions {
    showSkeleton?: boolean
    updateSelection?: boolean
    refetchFormat?: boolean
}

export const useOmanCheckout = () => {
    const [step, setStep] = useAtom(checkoutStepAtom)
    const [referralId, setReferralId] = useAtom(checkoutReferralIdAtom)
    const [, setReferralInfo] = useAtom(checkoutReferralInfoAtom)
    const [uuid, setUuid] = useAtom(checkoutUuidAtom)
    const [period, setPeriod] = useAtom(checkoutPeriodAtom)
    const [loading, setLoading] = useAtom(checkoutLoadingAtom)
    const [errors, setErrors] = useAtom(checkoutErrorsAtom)

    const [duplicatedEmail, setDuplicatedEmail] = useState(false)
    const [duplicatedPhone, setDuplicatedPhone] = useState(false)

    const v2Cart = useAtomValue(v2CartAtom)
    const cart = useAtomValue(cartAtom)
    const hotFormatRaw = useAtomValue(hotFormatRawAtom)
    const authentication = useAtomValue(authenticationState)
    const customer = useAtomValue(customerState)
    const userStatus = useAtomValue(userStatusAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const setFeelgreatSettings = useSetAtom(feelgreatSettingsAtom)
    const setCart = useSetAtom(cartAtom)
    const setV2Cart = useSetAtom(v2CartAtom)
    const setCartEdited = useSetAtom(cartEditedAtom)
    const setFormatData = useSetAtom(omanFormatDataAtom)
    const setIsLoadingFormat = useSetAtom(isLoadingOmanFormatAtom)
    const setSelectedShippingMethod = useSetAtom(omanSelectedShippingMethodAtom)
    const setDeliveryFreight = useSetAtom(omanDeliveryFreightAtom)
    const navigate = useNavigate()
    const { login } = useLogin({ onSuccess: () => {} })

    const isAuthenticated = !!(authentication.token && authentication.baId)

    // Always tracks the latest authentication — used inside async callbacks where
    // the closure would otherwise hold a stale pre-login snapshot.
    const authLatestRef = useRef(authentication)
    authLatestRef.current = authentication

    /**
     * Serialises the active cart to a JSON string for API calls.
     *
     * Priority:
     * 1. V2 cart (Oman new-format) — serialised as array of V2CartItem
     * 2. V1 cart (legacy cartAtom) — serialised as { purchaseType: { flavorKey: qty } }
     * 3. Post-refresh fallback: reuse shipment_packs from the last format response
     */
    const buildShipmentPacksString = useCallback((): string => {
        if (v2Cart.length > 0) return JSON.stringify(v2Cart)

        const v1Entries = Object.entries(cart).filter(([, flavors]) => Object.values(flavors).some(qty => qty > 0))
        if (v1Entries.length > 0) {
            const serialized: Record<string, Record<string, number>> = {}
            for (const [purchaseType, flavors] of v1Entries) {
                const bucket: Record<string, number> = {}
                for (const [flavor, qty] of Object.entries(flavors)) {
                    if (qty <= 0) continue
                    bucket[flavor] = qty
                }
                if (Object.keys(bucket).length > 0) serialized[purchaseType] = bucket
            }
            if (Object.keys(serialized).length > 0) return JSON.stringify(serialized)
        }

        const fromFormat = hotFormatRaw?.ushop?.detail_page?.shipment_packs
        if (fromFormat) return fromFormat
        return '[]'
    }, [v2Cart, cart, hotFormatRaw])

    const getLoginInfo = useCallback(
        () => ({
            ba_status: userStatus ?? readStoredUserStatus() ?? customerHelpers.getStatus(customer) ?? '',
            login_id: authentication.baId ?? '',
            login_name: customer.humanName?.['fullName@en'] || customer.humanName?.fullName || '',
            login_name_native: customer.humanName?.fullName || ''
        }),
        [userStatus, customer, authentication]
    )

    const hydrateCartFromFormat = useCallback(
        (rawFormat: Record<string, unknown>) => {
            const packs = getShipmentPacks(rawFormat)
            if (!Array.isArray(packs)) return
            setCart({})
            setV2Cart(packs.filter(isV2CartItem))
        },
        [setCart, setV2Cart]
    )

    const hasCart = v2Cart.length > 0 || Object.values(cart).some(flavors => Object.values(flavors).some(qty => qty > 0))

    // ─── Guest: validate referral + create UUID → show form ───────────────────
    const validateAndProceed = useCallback(
        async (inputReferralId: string) => {
            if (!hasCart) {
                navigate('/')
                return
            }
            setLoading(true)
            setErrors([])
            try {
                const referralData = await validateOmanReferrer(inputReferralId)
                if (!referralData?.isAllow) {
                    analytics.track('checkoutReferralRejected', { reason: 'not_allowed' })
                    setErrors(['referral_not_found'])
                    return
                }

                setReferralId(inputReferralId)
                setReferralInfo({
                    baId: String(referralData.baId),
                    displayName: localize(referralData.displayName),
                    baStatus: referralData.baStatus
                })

                const [updatedSettings, entryPeriod] = await Promise.all([fetchFeelGreatSettings('OMN', referralData.baStatus), fetchOmanEntryPeriod()])
                setFeelgreatSettings(updatedSettings)
                setPeriod(entryPeriod)

                const uuidData = await createOmanUuid({
                    type: 'enroll',
                    shipment_packs: buildShipmentPacksString(),
                    period: entryPeriod,
                    medium: 'Internet',
                    agent: 'uFeelGreat',
                    platform: 'Android',
                    shipment_options: selectedShippingMethod,
                    referral_id: inputReferralId
                })

                if (!uuidData?.uuid) {
                    analytics.track('checkoutSessionFailed', { flow: 'enroll_referral', reason: 'uuid_creation_failed' })
                    setErrors(['uuid_creation_failed'])
                    return
                }
                setUuid(uuidData.uuid)
                analytics.track('checkoutSessionStarted', { flow: 'enroll_referral', shipping_method: selectedShippingMethod, uuid: uuidData.uuid })
                navigate(`/checkout/create-account?uuid=${uuidData.uuid}`)
            } catch {
                analytics.track('checkoutSessionFailed', { flow: 'enroll_referral', reason: 'network_error' })
                setErrors(['network_error'])
            } finally {
                setLoading(false)
            }
        },
        [
            buildShipmentPacksString,
            hasCart,
            navigate,
            selectedShippingMethod,
            setErrors,
            setFeelgreatSettings,
            setLoading,
            setPeriod,
            setReferralId,
            setReferralInfo,
            setUuid
        ]
    )

    // ─── Guest: create UUID without referral → navigate to create-account ──────
    const createGuestUuidAndProceed = useCallback(async () => {
        if (!hasCart) {
            navigate('/')
            return
        }
        setLoading(true)
        setErrors([])
        try {
            const entryPeriod = await fetchOmanEntryPeriod()
            setPeriod(entryPeriod)

            const uuidData = await createOmanUuid({
                type: 'enroll',
                shipment_packs: buildShipmentPacksString(),
                period: entryPeriod,
                medium: 'Internet',
                agent: 'uFeelGreat',
                platform: 'Android',
                shipment_options: selectedShippingMethod,
                ...(referralId ? { referral_id: referralId } : {})
            })

            if (!uuidData?.uuid) {
                analytics.track('checkoutSessionFailed', { flow: 'enroll', reason: 'uuid_creation_failed' })
                setErrors(['uuid_creation_failed'])
                return
            }
            setUuid(uuidData.uuid)
            analytics.track('checkoutSessionStarted', { flow: 'enroll', shipping_method: selectedShippingMethod, uuid: uuidData.uuid })
            navigate(`/checkout/create-account?uuid=${uuidData.uuid}`)
        } catch {
            analytics.track('checkoutSessionFailed', { flow: 'enroll', reason: 'network_error' })
            setErrors(['network_error'])
        } finally {
            setLoading(false)
        }
    }, [buildShipmentPacksString, hasCart, navigate, referralId, selectedShippingMethod, setErrors, setLoading, setPeriod, setUuid])

    // ─── Logged-in member: create UUID → go to shipping ───────────────────────
    // reload=true does a full page load (window.location) instead of SPA nav —
    // used by the guest→member login switch so the shipping page re-inits cleanly
    // from the freshly-stored auth (SPA nav raced with the auth update).
    const createUuidAndProceed = useCallback(
        async (reload = false) => {
            if (!authentication.token || !authentication.baId) {
                window.location.replace(getFullPath('/'))
                return
            }
            if (!hasCart) {
                navigate('/')
                return
            }
            setLoading(true)
            setErrors([])
            try {
                const entryPeriod = await fetchOmanEntryPeriod()
                setPeriod(entryPeriod)

                const uuidData = await createOmanUuid({
                    type: 'shop',
                    shipment_packs: buildShipmentPacksString(),
                    period: entryPeriod,
                    medium: 'Internet',
                    agent: 'uFeelGreat',
                    platform: getOperatingSystemName(),
                    shipment_options: selectedShippingMethod,
                    token: authentication.token,
                    ...getLoginInfo()
                })

                if (!uuidData?.uuid) {
                    analytics.track('checkoutSessionFailed', { flow: 'shop', reason: 'uuid_creation_failed' })
                    setErrors(['uuid_creation_failed'])
                    return
                }
                setUuid(uuidData.uuid)
                analytics.track('checkoutSessionStarted', { flow: 'shop', shipping_method: selectedShippingMethod, uuid: uuidData.uuid })
                if (reload) {
                    window.location.assign(getFullPath(`/checkout/shipping?uuid=${uuidData.uuid}`))
                } else {
                    navigate(`/checkout/shipping?uuid=${uuidData.uuid}`)
                }
            } catch {
                analytics.track('checkoutSessionFailed', { flow: 'shop', reason: 'network_error' })
                setErrors(['network_error'])
            } finally {
                setLoading(false)
            }
        },
        [authentication, buildShipmentPacksString, getLoginInfo, hasCart, navigate, selectedShippingMethod, setErrors, setLoading, setPeriod, setUuid]
    )

    // ─── Load format data from API (called once uuid is in URL) ───────────────
    const loadFormatData = useCallback(
        async (uuidValue: string) => {
            setIsLoadingFormat(true)
            setErrors([])
            try {
                const data = await getOmanFormat(uuidValue)
                hydrateCartFromFormat(data)
                setCartEdited(false)
                setFormatData({ ...data, uuid: uuidValue })
                setSelectedShippingMethod('delivery')
                setDeliveryFreight(extractFreight(data))
                setUuid(uuidValue)
                setStep('form')
            } catch (e) {
                // A dead uuid bounces the shopper to the landing page with no trace otherwise.
                analytics.track('checkoutFormatLoadFailed', { step: 'load', uuid: uuidValue, is_uuid_expired: isOmanUuidExpiredError(e) })
                if (isOmanUuidExpiredError(e)) {
                    navigate('/', { replace: true })
                    return
                }
                setErrors(['format_load_failed'])
            } finally {
                setIsLoadingFormat(false)
                setLoading(false)
            }
        },
        [
            hydrateCartFromFormat,
            navigate,
            setCartEdited,
            setDeliveryFreight,
            setErrors,
            setFormatData,
            setIsLoadingFormat,
            setLoading,
            setSelectedShippingMethod,
            setStep,
            setUuid
        ]
    )

    const syncShippingMethod = useCallback(
        async (
            shipmentMethod: TOmanShippingMethodId,
            skipValidate = true,
            contact?: TOmanShippingContact,
            options: SyncShippingMethodOptions = {}
        ): Promise<boolean> => {
            if (!uuid) {
                if (options.updateSelection !== false) setSelectedShippingMethod(shipmentMethod)
                return true
            }

            const setBusy = options.showSkeleton === false ? setLoading : setIsLoadingFormat
            if (options.updateSelection !== false) setSelectedShippingMethod(shipmentMethod)
            setBusy(true)
            setErrors([])
            try {
                const rawFormat =
                    options.refetchFormat === false
                        ? await updateOmanShippingMethod(uuid, shipmentMethod, skipValidate, contact)
                        : await syncOmanShippingMethodFormat(uuid, shipmentMethod, skipValidate, contact)

                if (rawFormat?.success === false) {
                    analytics.track('checkoutFormatLoadFailed', { step: 'shipping_sync', shipping_method: shipmentMethod, reason: 'result_error', uuid })
                    setErrors(parseOmanResultError(rawFormat))
                    return false
                }

                if (options.refetchFormat !== false) {
                    setFormatData({ ...rawFormat, uuid })
                }
                if (options.updateSelection !== false) {
                    setSelectedShippingMethod(shipmentMethod)
                }
                if (options.refetchFormat !== false && shipmentMethod !== 'pickup') {
                    setDeliveryFreight(extractFreight(rawFormat))
                }
                analytics.track('checkoutShippingMethodChanged', { shipping_method: shipmentMethod, uuid })
                return true
            } catch (e) {
                analytics.track('checkoutFormatLoadFailed', {
                    step: 'shipping_sync',
                    shipping_method: shipmentMethod,
                    is_uuid_expired: isOmanUuidExpiredError(e),
                    uuid
                })
                if (isOmanUuidExpiredError(e)) {
                    navigate('/', { replace: true })
                    return false
                }
                setErrors(['format_load_failed'])
                return false
            } finally {
                setBusy(false)
            }
        },
        [navigate, setDeliveryFreight, setErrors, setFormatData, setIsLoadingFormat, setLoading, setSelectedShippingMethod, uuid]
    )

    // ─── Guest: create account + shipping ─────────────────────────────────────
    const submitGuestForm = useCallback(
        async (form: TOmanCheckoutForm) => {
            setLoading(true)
            setDuplicatedEmail(false)
            setDuplicatedPhone(false)
            try {
                const result = await createOmanAccount({
                    type: referralId ? 'enrollWithReferral' : 'enroll',
                    uuid,
                    referral_id: referralId || undefined,
                    firstname_en: form.firstname,
                    lastname_en: form.lastname,
                    email: form.email,
                    phone: form.phone,
                    password: form.password,
                    address: form.address1,
                    address2: form.address2,
                    city: form.city,
                    state: form.state,
                    country: form.country,
                    shipment_options: selectedShippingMethod,
                    period,
                    shipment_packs: buildShipmentPacksString()
                })

                const isDupEmail = (result as Record<string, unknown>)?.isDuplicatedEmail === true
                const isDupPhone = (result as Record<string, unknown>)?.isDuplicatedPhone === true
                if (isDupEmail || isDupPhone) {
                    analytics.track('checkoutAccountFailed', {
                        reason: 'duplicate',
                        duplicate_field: isDupEmail && isDupPhone ? 'both' : isDupEmail ? 'email' : 'phone',
                        uuid
                    })
                    setDuplicatedEmail(isDupEmail)
                    setDuplicatedPhone(isDupPhone)
                    return
                }

                if (result?.success === false) {
                    analytics.track('checkoutAccountFailed', { reason: 'result_error', uuid })
                    setErrors(parseOmanResultError(result))
                    return
                }

                await login(form.email, form.password)

                // Mirror what submitMemberShipping does for members: associate the
                // newly-obtained token with the UUID so createPaymentV3 can validate
                // the Authorization-hydra header. Without this the backend rejects
                // the payment request as unauthorized.
                const freshAuth = authLatestRef.current
                if (freshAuth.token && freshAuth.baId) {
                    const fullname = `${form.firstname} ${form.lastname}`.trim()
                    await putOmanMemberShipping({
                        uuid,
                        fullname,
                        address: form.address1,
                        address2: form.address2 || undefined,
                        city: form.city,
                        state: form.state,
                        country: form.country,
                        phone: form.phone || undefined,
                        email: form.email || undefined,
                        shipment_options: selectedShippingMethod,
                        token: freshAuth.token,
                        type: 'shop',
                        shipment_packs: buildShipmentPacksString(),
                        ba_status: readStoredUserStatus() ?? '',
                        login_id: freshAuth.baId,
                        login_name: fullname,
                        login_name_native: fullname
                    })
                }

                analytics.track('checkoutAccountCreated', { has_referral: Boolean(referralId), shipping_method: selectedShippingMethod, uuid })
                setIsLoadingFormat(true)
                navigate(`/checkout/payment?uuid=${uuid}`)
            } catch (e) {
                analytics.track('checkoutAccountFailed', { reason: 'exception', uuid })
                setErrors(parseOmanAxiosError(e))
            } finally {
                setLoading(false)
            }
        },
        [buildShipmentPacksString, login, navigate, period, referralId, selectedShippingMethod, setErrors, setIsLoadingFormat, setLoading, uuid]
    )

    // ─── Member: save shipping address ────────────────────────────────────────
    const submitMemberShipping = useCallback(
        async (form: TOmanShippingForm) => {
            if (!authentication.token) {
                window.location.replace(getFullPath('/'))
                return
            }
            setLoading(true)
            setErrors([])
            analytics.track('checkoutShippingSubmitted', { shipping_method: selectedShippingMethod, uuid })
            try {
                const result = await putOmanMemberShipping({
                    uuid,
                    fullname: form.fullname,
                    address: form.address1,
                    address2: form.address2 || undefined,
                    city: form.city,
                    state: form.state,
                    country: form.country,
                    phone: form.phone || undefined,
                    email: form.email || undefined,
                    shipment_options: selectedShippingMethod,
                    token: authentication.token,
                    type: 'shop',
                    shipment_packs: buildShipmentPacksString(),
                    ...getLoginInfo()
                })

                if (result?.success === false) {
                    analytics.track('checkoutShippingFailed', { shipping_method: selectedShippingMethod, reason: 'result_error', uuid })
                    setErrors(parseOmanResultError(result))
                    return
                }

                navigate(`/checkout/payment?uuid=${uuid}`)
            } catch (e) {
                analytics.track('checkoutShippingFailed', { shipping_method: selectedShippingMethod, reason: 'exception', uuid })
                setErrors(parseOmanAxiosError(e))
            } finally {
                setLoading(false)
            }
        },
        [authentication.token, buildShipmentPacksString, getLoginInfo, navigate, selectedShippingMethod, setErrors, setLoading, uuid]
    )

    // ─── Load address history for logged-in member ────────────────────────────
    const loadAddressHistory = useCallback(async () => {
        if (!authentication.token || !authentication.baId) {
            window.location.replace(getFullPath('/'))
            return []
        }
        try {
            const data = await fetchOmanAddressHistory(authentication.baId, authentication.token)
            const items = data.items ?? []
            if (data.mainAddress) {
                items.unshift(data.mainAddress as TAddressHistoryResponse['items'][number])
            }
            return items
        } catch {
            return []
        }
    }, [authentication.baId, authentication.token])

    return {
        step,
        setStep,
        referralId,
        uuid,
        loading,
        errors,
        setErrors,
        isAuthenticated,
        duplicatedEmail,
        duplicatedPhone,
        loadFormatData,
        syncShippingMethod,
        createUuidAndProceed,
        createGuestUuidAndProceed,
        validateAndProceed,
        submitGuestForm,
        submitMemberShipping,
        loadAddressHistory
    }
}
